import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Upload } from "lucide-react";
import axios from "axios";
import { formatApiError, API, authHeaders } from "@/hooks/useAdminData";

export default function GalleryManager({ gallery, loadData }) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryForm, setGalleryForm] = useState({ title: "", description: "", image_url: "", file: null });

  const addGalleryItem = async () => {
    try {
      if (galleryForm.file) {
        const formData = new FormData();
        formData.append("file", galleryForm.file);
        await axios.post(`${API}/gallery/upload?title=${encodeURIComponent(galleryForm.title || galleryForm.file.name)}&description=${encodeURIComponent(galleryForm.description)}`, formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data", ...authHeaders() },
        });
      } else if (galleryForm.image_url) {
        await axios.post(`${API}/gallery`, {
          title: galleryForm.title,
          description: galleryForm.description,
          image_url: galleryForm.image_url,
        }, { withCredentials: true, headers: authHeaders() });
      } else {
        alert("Please upload a photo or provide an image URL");
        return;
      }
      setGalleryForm({ title: "", description: "", image_url: "", file: null });
      setGalleryOpen(false);
      loadData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteGalleryItem = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    await axios.delete(`${API}/gallery/${id}`, { withCredentials: true, headers: authHeaders() }).catch(() => {});
    loadData();
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Gallery Images ({gallery.length})</h3>
        <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
          <DialogTrigger asChild>
            <Button className="btn-secondary text-sm" data-testid="add-gallery-btn"><Plus size={14} className="mr-1" /> Add Image</Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle>Add Gallery Image</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm">Title</Label>
                <Input value={galleryForm.title} onChange={e => setGalleryForm({...galleryForm, title: e.target.value})} className="rounded-xl mt-1" placeholder="Photo title" data-testid="gallery-title-input" />
              </div>
              <div>
                <Label className="text-sm font-medium flex items-center gap-2"><Upload size={14} /> Upload Photo</Label>
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={e => setGalleryForm({...galleryForm, file: e.target.files[0] || null})}
                  className="rounded-xl mt-1"
                  data-testid="gallery-file-input"
                />
                {galleryForm.file && (
                  <p className="text-xs text-green-600 mt-1">Selected: {galleryForm.file.name}</p>
                )}
              </div>
              <div className="relative">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-gray-400">OR paste a URL</span></div>
              </div>
              <div>
                <Label className="text-sm">Image URL (optional)</Label>
                <Input value={galleryForm.image_url} onChange={e => setGalleryForm({...galleryForm, image_url: e.target.value})} className="rounded-xl mt-1" placeholder="https://..." data-testid="gallery-url-input" />
              </div>
              <div>
                <Label className="text-sm">Description</Label>
                <Textarea value={galleryForm.description} onChange={e => setGalleryForm({...galleryForm, description: e.target.value})} className="rounded-xl mt-1" data-testid="gallery-desc-input" />
              </div>
              <Button onClick={addGalleryItem} className="btn-primary w-full" data-testid="gallery-submit-btn">
                {galleryForm.file ? "Upload & Add Photo" : "Add Image"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Preview</TableHead><TableHead>Title</TableHead><TableHead>Description</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {gallery.map(item => {
              const imgSrc = item.image_url;
              return (
              <TableRow key={item.id}>
                <TableCell><img src={imgSrc} alt={item.title} className="w-16 h-12 object-cover rounded-lg" /></TableCell>
                <TableCell className="font-medium">{item.title}</TableCell>
                <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">{item.description}</TableCell>
                <TableCell>
                  <button onClick={() => deleteGalleryItem(item.id)} className="text-red-500 hover:text-red-700 p-1" data-testid={`delete-gallery-${item.id}`}>
                    <Trash2 size={16} />
                  </button>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {gallery.length === 0 && <p className="text-center text-gray-400 py-8 text-sm">No gallery images yet.</p>}
      </div>
    </>
  );
}
